import React, { FC, ReactNode, useState }                                                                  from 'react';
import { Align, CorvalUIColor, FlexAlign, FlexFlow, Position, SemanticUIColor, Size }                      from '../../../types';
import { CenteredContentContainer, Flexbox, FlexItem, RouteButton, StyledContainer, StyledContainerProps } from '../../containers';
import { Header, List, Transition }                                                                        from 'semantic-ui-react';
import { CHARACTER_VISUAL_AID, CorvalCharacterVisualAid }                                                  from '../product/CorvalCharacterVisualAid';
import _                                                                                                   from 'underscore';
import { HexagonShape, HexagonShapeProps }                                                                 from '../util/visual/HexagonShape';
import { RouterUtility }                                                                                   from '../../../util/network/RouterUtility';
import { useHistory }                                                                                      from 'react-router';
import classNames                                                                                          from 'classnames';

export interface DescriptiveVisualNavItem {
	label: string | ReactNode,
	description: string | ReactNode,
	routePath: string,
	visualAid?: CHARACTER_VISUAL_AID | null
}

interface Props {

	/* Large titling at the top */
	header?: string | ReactNode,

	/* Introductory copy for the choices */
	introCopy?: string | ReactNode,

	/* When specified, a graphic in the middle of the nav items */
	visualAid: CHARACTER_VISUAL_AID | null,

	/* Objects representing the nav links, description text, and any accompanying visual aids  */
	navObjects: DescriptiveVisualNavItem[],

	/* Whether to use a descriptive center hex for descriptions */
	useDescriptionShape?: boolean

	/* Color of the descriptive center hex, when used */
	descriptionShapeColor?: HexagonShapeProps['color'],

	/* Color of the descriptive text, when used */
	descriptionTextColor?: StyledContainerProps[ 'textColor'],

	/* Color of the center hex behind the visual aids */
	accentVisualAidShapeColor?: HexagonShapeProps['color'],

	/* Color of the center hex behind the everything */
	accentBGShapeColor?: HexagonShapeProps['color'],

	/* Number height to base individual visual aid dimensions on. */
	visualAidHeight?: number,

	/* Whether to show a border on hover with multiple visual aids */
	borderHighlight?: boolean

	/* Border color to use on hover with multiple visual aids */
	borderHighlighColor?: HexagonShapeProps['color']

}

const defaultProps: Props = {
	header:                    null,
	introCopy:                 null,
	visualAid:                 CHARACTER_VISUAL_AID.CORVAL_SHAPES_CONVERSING_WITH_LAPTOP,
	navObjects:                [
		{
			description: 'View a list of your current projects.',
			label: 'Home',
			routePath:   RouterUtility.getRoute( 'DASHBOARD' ),
			visualAid:   null
		},
	],
	useDescriptionShape:       true,
	descriptionShapeColor:     SemanticUIColor.PURPLE,
	descriptionTextColor:      CorvalUIColor.WHITE,
	accentVisualAidShapeColor: CorvalUIColor.TEALBACKGROUND,
	accentBGShapeColor:        CorvalUIColor.WHITE,
	visualAidHeight:           215,
	borderHighlight:           true,
	borderHighlighColor:       SemanticUIColor.PURPLE,
}

interface NavButtonComponentProps {
	item: DescriptiveVisualNavItem
}

const NavButtonComponent: FC<NavButtonComponentProps> = ( props ) => {

	return (
		_.isString( props.item.label )
		? <RouteButton primary size={ 'large' } to={ props.item.routePath }>{ props.item.label }</RouteButton>
		: <>{ props.item.label }</>
	)
}

const DescriptiveVisualNav: FC<Props> = ( props ) => {

	let history                         = useHistory(),
	    [ description, setDescription ] = useState<string | ReactNode>( null );

	let navVisualAids         = _.chain( props.navObjects ).pluck( 'visualAid' ).without( null ).value(),
	    hasNavItems           = ( props.navObjects.length > 0 ),
	    hasSeparateVisualAids = ( navVisualAids.length === props.navObjects.length );

	return (

		<StyledContainer padding={ Size.LG }>

			<Flexbox direction={ FlexFlow.COLS } align={ FlexAlign.CENTER }>

				<FlexItem>

					<div style={ { width: '100%', minHeight: props.visualAidHeight } } className={ 'position--relative fade-in' }>

						<StyledContainer position={ Position.RELATIVE } zIndex={ 300 } textAlign={ Align.CENTER }>

							{ props.header && <Header as={ 'h1' } size={ 'huge' } color={ 'blue' }>{ props.header }</Header> }
							{ props.introCopy && <p>{ props.introCopy }</p> }

							{/* Single visual aid */ }
							{ !hasSeparateVisualAids && props.visualAid &&
							<CorvalCharacterVisualAid type={ props.visualAid } margin={ Size.LG } width={ 'auto' } height={ props.visualAidHeight }/> }

							{/* Multiple visual aids */ }
							{ hasSeparateVisualAids && hasNavItems && (

								<Flexbox cols={ props.navObjects.length } gutterSize={ Size.XS }>

									{ props.navObjects.map( ( item: DescriptiveVisualNavItem, index: number ) => (

										<FlexItem key={ index } contentClasses={ 'cursor--pointer' } className={ classNames( {
											'border-lg':                                          props.borderHighlight,
											'border--hover':                                      props.borderHighlight,
											[ `border--colored-${ props.borderHighlighColor }` ]: props.borderHighlight,
											'container--rounded':                                 props.borderHighlight,
										} ) }>

											<StyledContainer textAlign={ Align.CENTER } margin={ [ Size.MD, null ] } style={ { minHeight: props.visualAidHeight } }>

												<CorvalCharacterVisualAid
													type={ item.visualAid }
													height={ 'auto' }
													width={ 'auto' }
													style={ { maxHeight: props.visualAidHeight, display: 'inline-block' } }
													onClick={ () => history.push( item.routePath ) }/>

											</StyledContainer>
											<StyledContainer textAlign={ Align.CENTER } margin={ [ Size.MD, null ] }>

												<p className={ 'margin-md-horizontal' }>{ item.description }</p>
												<NavButtonComponent item={ item }/>

											</StyledContainer>
										</FlexItem>

									) ) }
								</Flexbox>
							) }


						</StyledContainer>

						{ !hasSeparateVisualAids && props.useDescriptionShape && (
							<Transition duration={ 200 } visible={ !_.isNull( description ) } animation={ 'fade up' }>
								<StyledContainer position={ Position.ABSOLUTE } zIndex={ 400 } top={ 150 } left={ 'calc(50% - 100px)' }>
									<HexagonShape size={ 200 } color={ props.descriptionShapeColor } rotation={ 0 }>
										<CenteredContentContainer basis={ 75 } className={ `container--fill-area text--colored-${ props.descriptionTextColor }` } align={ FlexAlign.CENTER }>
											{ _.isString( description ) ? <p>{ description }</p> : description }
										</CenteredContentContainer>
									</HexagonShape>
								</StyledContainer>
							</Transition>
						) }

						<StyledContainer position={ Position.ABSOLUTE } zIndex={ 200 } top={ 0 } left={ 'calc(50% - 200px)' } className={ 'rotate-slowly' }>

							<HexagonShape size={ 400 } color={ props.accentVisualAidShapeColor } rotation={ 0 }/>

						</StyledContainer>
						<StyledContainer position={ Position.ABSOLUTE } zIndex={ 100 } top={ -100 } left={ 'calc(50% - 300px)' } className={ 'rotate-very-slowly' }>

							<HexagonShape size={ 600 } color={ props.accentBGShapeColor } rotation={ 30 }/>

						</StyledContainer>

					</div>

				</FlexItem>

				{ !hasSeparateVisualAids && (

					<FlexItem>

						{ hasNavItems && (

							<List divided horizontal relaxed={ 'very' }>

								{ props.navObjects.map( ( item: DescriptiveVisualNavItem, index: number ) => (

									<List.Item key={ index }>

										{ props.useDescriptionShape ? (

											<div onMouseEnter={ () => setDescription( item.description ) }
											     onMouseLeave={ () => setDescription( null ) }>
												<NavButtonComponent item={ item }/>
											</div>

										) : (

											  <StyledContainer textAlign={ Align.CENTER }>
												  <p className={ 'margin-md-horizontal' }>{ item.description }</p>
												  <NavButtonComponent item={ item }/>
											  </StyledContainer>

										  ) }

									</List.Item>

								) ) }

							</List>

						) }

					</FlexItem>
				) }

			</Flexbox>

		</StyledContainer>
	);
};

DescriptiveVisualNav.defaultProps = defaultProps;

export { DescriptiveVisualNav }
